<!--
  La aplicación dibujada.

  La misma vista sirve para el enlace público y para la previsualización del
  panel. Lo único que cambia es de donde sale el HTML de cada página y si se
  cuenta lo que el documento hace mal: un visitante nunca debe ver eso.
-->
<script lang="ts">
  import type { AppBundle } from "@shared/types";
  import type PocketBase from "pocketbase";
  import type { Snippet } from "svelte";

  import { paletteAttrs } from "../lib/appTheme";
  import type { DocLoader } from "../lib/htmlDocs";
  import { navLayout } from "../lib/navLayout.svelte";
  import { pageMode } from "../lib/pageMode.svelte";
  import AppSidebar from "./app/AppSidebar.svelte";
  import PageView from "./PageView.svelte";
  import ModeToggle from "./ui/ModeToggle.svelte";

  let {
    bundle,
    client,
    makePageLoader,
    pageSlug,
    onOpenPage,
    showIssues = false,
    banner,
    footer: extra,
    account,
  }: {
    bundle: AppBundle;
    client: PocketBase;
    /** De donde sale el HTML de una página. */
    makePageLoader?: (pageId: string) => DocLoader;
    pageSlug?: string;
    onOpenPage: (page: AppBundle["pages"][number]) => void;
    showIssues?: boolean;
    banner?: Snippet;
    /** Lo que cada quien añade al pie del sidebar: quien entró, y su salida. */
    footer?: Snippet;
    /** Quien entró, en la barra de arriba de un teléfono. */
    account?: Snippet;
  } = $props();

  const pages = $derived(bundle.pages);
  const page = $derived(
    // Un enlace a un grupo no debe abrirlo como si tuviera documento.
    pages.find((p) => !p.separator && p.slug === pageSlug) ??
      pages.find((p) => !p.separator && p.isHome) ??
      pages.find((p) => !p.separator),
  );

  // Columna o barra de arriba es de quien mira, y se recuerda por aplicación.
  const nav = navLayout(() => bundle.app.id);
  /*
   * Claro u oscuro lo elige quien mira, no quien construye. Lo que si es de la
   * aplicación son sus dos colores, y van en este mismo elemento a propósito:
   * ver `lib/appTheme.ts`.
   */
  const mode = pageMode(() => bundle.app.id);

  /*
   * El cargador del documento tiene que durar lo que dure la página: uno nuevo
   * por dibujado reiniciaría el marco sin parar. Por eso depende solo del id.
   */
  const loadDoc = $derived((makePageLoader ?? (() => async () => ""))(page?.id ?? ""));
</script>

<div
  id="published-app"
  data-theme={mode.mode}
  class="app-published flex h-full flex-col"
  {...paletteAttrs(bundle.app.theme)}
>
  {@render banner?.()}
  <!--
    Una sola caja con el documento dentro. El sidebar va a su lado en columna,
    o encima como cajón cuando la navegación va en la barra de arriba.
  -->
  <div class="app-published-frame">
    <AppSidebar
      app={bundle.app}
      {pages}
      activeId={page?.id}
      onOpen={onOpenPage}
      pinned
      layout={nav.layout}
      onToggleLayout={nav.toggle}
      footer={extra}
      {account}
    >
      {#snippet headerActions()}
        <!--
          Claro u oscuro en una aplicación publicada, junto al nombre de la
          aplicación arriba: es un ajuste de quien mira, no de la aplicación.
          Lo elegido se recuerda para esta aplicación en este navegador;
          mientras nadie elija, manda lo que pida el sistema.
        -->
        <ModeToggle
          dark={mode.mode === "dark"}
          onToggle={(next) => mode.set(next)}
          tipLight="Fondo claro"
          tipDark="Fondo oscuro"
          class="btn-page-mode"
        />
      {/snippet}
    </AppSidebar>

    <main id="published-main" class="main-published">
      {#if page?.doc}
        <PageView
          {page}
          appId={bundle.app.id}
          {client}
          {loadDoc}
          roles={bundle.roles ?? []}
          themeKey={`${JSON.stringify(bundle.app.theme)}:${mode.mode}:${nav.layout}`}
          {showIssues}
        />
      {:else}
        <p class="app-published-empty text-center">Esta página está vacía.</p>
      {/if}
    </main>
  </div>
</div>

<style>
  .app-published {
    /* El fondo lo pone quien cambia el tema: sin él, en lo que no cubre
       nadie --encima de la columna, debajo de la barra-- se veía el del
       panel, que puede ir en el otro modo. */
    background: var(--bg-level1);

    & .app-published-frame {
      position: relative;
      display: flex;
      min-height: 0;
      flex: 1 1 0%;

      /* Con la barra de arriba (`AppSidebar`) el documento empieza debajo de
         ella, no por detrás: lo que una página fije arriba de su ventana
         --un panel, un modal, una cabecera-- queda a la vista sin que la
         página sepa que hay barra. La sombra al desplazarse la dibuja el
         documento (`page.css`). */
      &:has(> :global(.topbar-app-mobile)) {
        --plane-topbar-shadow: 0.5rem;

        & .main-published {
          padding-top: 3rem;
        }
      }

      & .main-published {
        height: 100%;
        min-width: 0;
        flex: 1 1 0%;
        background: var(--bg-level1);
      }
    }
  }

  /* La página vacía se anuncia sobria, centrada. */
  .app-published-empty {
    padding: 4rem 0;
    font-size: var(--text-sm);
    line-height: var(--text-sm--line-height);
    color: var(--text-muted);
  }
</style>
